<template>
  <main class="sf-page">
    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
    <div v-else-if="error" class="sf-section sf-section--cream">
      <div class="sf-container sf-error">
        <div class="sf-error__icon-wrap"><X /></div>
        <h2 class="sf-h2">Formation non trouvée</h2>
        <router-link to="/formations"><button class="sf-btn sf-btn--terra"><ArrowLeft class="sf-btn__icon" />Retour</button></router-link>
      </div>
    </div>

    <template v-else-if="formation">
      <!-- Hero -->
      <div class="sf-page-hero">
        <div class="sf-page-hero__inner">
          <div class="sf-formation-badges">
            <span class="fm-badge-hero">{{ formation.level }}</span>
            <span class="fm-badge-hero fm-badge-hero--muted">{{ formation.duration }}</span>
            <span class="fm-badge-hero fm-badge-hero--green">{{ formation.price||'Gratuit' }}</span>
            <span v-if="formation.status==='complet'" class="fm-badge-hero fm-badge-hero--red">Complet</span>
          </div>
          <h1 class="sf-h1" style="margin:.75rem 0 1rem">{{ formation.title }}</h1>
          <p class="sf-page-hero__sub">{{ formation.description }}</p>
        </div>
      </div>

      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <router-link to="/formations" class="sf-back-link"><ArrowLeft />Retour aux formations</router-link>

          <div class="sf-layout" style="margin-top:2rem">
            <!-- Main -->
            <div class="sf-main">
              <!-- Objectifs -->
              <div v-if="formation.objectives?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Objectifs de la formation</h3></div>
                <div class="sf-sidebar-card__body">
                  <ul class="sf-check-list">
                    <li v-for="(obj,i) in formation.objectives" :key="i"><CheckCircle />{{ obj }}</li>
                  </ul>
                </div>
              </div>

              <!-- Programme -->
              <div v-if="formation.program?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Programme détaillé</h3></div>
                <div class="sf-sidebar-card__body">
                  <div v-for="(mod,i) in formation.program" :key="i" class="sf-program-item">
                    <div class="sf-program-item__head">
                      <span class="sf-program-item__title">{{ mod.module||mod.title }}</span>
                      <span class="fm-badge-hero fm-badge-hero--muted">{{ mod.duration }}</span>
                    </div>
                    <div class="sf-program-item__topics">
                      <span v-for="(t,j) in mod.topics" :key="j" class="sf-tag">{{ t }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Compétences -->
              <div v-if="formation.skills?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Compétences acquises</h3></div>
                <div class="sf-sidebar-card__body" style="display:flex;flex-wrap:wrap;gap:.5rem">
                  <span v-for="(sk,i) in formation.skills" :key="i" class="fm-skill-tag">{{ sk }}</span>
                </div>
              </div>

              <!-- Formateur -->
              <div v-if="formation.trainer" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Votre formateur</h3></div>
                <div class="sf-sidebar-card__body">
                  <div style="display:flex;align-items:center;gap:1rem">
                    <div class="sf-avatar" style="width:64px;height:64px;font-size:1.5rem">{{ getTrainerInitials(formation.trainer) }}</div>
                    <div>
                      <p style="font-family:var(--fd);font-size:1.2rem;font-weight:600;color:var(--ink)">{{ formation.trainer.name }}</p>
                      <p style="font-size:.85rem;color:var(--terra)">{{ formation.trainer.title }}</p>
                      <p style="font-size:.82rem;color:var(--ink-60);margin-top:.2rem">{{ formation.trainer.experience }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Inscription -->
              <div class="sf-sidebar-card" id="inscription">
                <div class="sf-sidebar-card__header">
                  <h3>Formulaire d'inscription</h3>
                  <p v-if="formation.status==='complet'" style="font-size:.8rem;color:var(--terra);margin-top:.25rem">Cette formation est complète.</p>
                </div>
                <div class="sf-sidebar-card__body">
                  <form @submit.prevent="submitRegistration" style="display:flex;flex-direction:column;gap:1rem">
                    <div class="sf-form-row">
                      <div><label class="sf-input-label sf-input-label--req">Prénom</label><input v-model="registrationForm.first_name" type="text" required :disabled="formation.status==='complet'||registrationLoading" class="sf-input" /></div>
                      <div><label class="sf-input-label sf-input-label--req">Nom</label><input v-model="registrationForm.last_name" type="text" required :disabled="formation.status==='complet'||registrationLoading" class="sf-input" /></div>
                    </div>
                    <div class="sf-form-row">
                      <div><label class="sf-input-label sf-input-label--req">Email</label><input v-model="registrationForm.email" type="email" required :disabled="formation.status==='complet'||registrationLoading" class="sf-input" /></div>
                      <div><label class="sf-input-label sf-input-label--req">Téléphone</label><input v-model="registrationForm.phone" type="tel" required :disabled="formation.status==='complet'||registrationLoading" class="sf-input" /></div>
                    </div>
                    <div><label class="sf-input-label sf-input-label--req">Lettre de motivation</label><textarea v-model="registrationForm.motivation" required rows="6" :disabled="formation.status==='complet'||registrationLoading" class="sf-input sf-textarea" placeholder="Expliquez pourquoi vous souhaitez rejoindre cette formation…"></textarea></div>
                    <button type="submit" class="sf-btn sf-btn--terra sf-btn--full" :disabled="registrationLoading||formation.status==='complet'" style="padding:.9rem">
                      {{ formation.status==='complet'?'Formation complète':'Envoyer ma candidature' }}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <!-- Sidebar -->
            <div class="sf-sidebar">
              <div class="sf-sidebar-card" style="position:sticky;top:80px">
                <div class="sf-sidebar-card__header"><h3>Informations pratiques</h3></div>
                <div class="sf-sidebar-card__body">
                  <div class="sf-info-rows">
                    <div class="sf-info-row"><div class="sf-info-row__icon"><Calendar /></div><div><p class="sf-info-row__label">Prochaine session</p><p class="sf-info-row__value">{{ formatDate(formation.next_session) }}</p></div></div>
                    <div class="sf-info-row"><div class="sf-info-row__icon"><Clock /></div><div><p class="sf-info-row__label">Horaires</p><p class="sf-info-row__value">{{ formation.schedule }}</p></div></div>
                    <div class="sf-info-row"><div class="sf-info-row__icon"><Users /></div><div><p class="sf-info-row__label">Places disponibles</p><p class="sf-info-row__value">{{ formation.remaining_spots||formation.capacity-(formation.enrolled||0) }} / {{ formation.capacity }}</p></div></div>
                    <div class="sf-info-row"><div class="sf-info-row__icon"><Award /></div><div><p class="sf-info-row__label">Certification</p><p class="sf-info-row__value">Reconnue</p></div></div>
                  </div>
                  <div v-if="formation.enrolled>0" style="margin-top:1.25rem;padding-top:1.25rem;border-top:1px solid var(--ink-20)">
                    <div class="sf-progress"><div class="sf-progress__track"><div class="sf-progress__fill" :style="{'--target':(formation.progress||Math.round(formation.enrolled/formation.capacity*100))+'%'}"></div></div><span class="sf-progress__pct">{{ formation.progress||Math.round(formation.enrolled/formation.capacity*100) }}%</span></div>
                  </div>
                </div>
              </div>
              <div class="sf-sidebar-card" style="margin-top:1.25rem;background:rgba(27,58,45,.06);border:1px solid rgba(27,58,45,.12)">
                <div class="sf-sidebar-card__body">
                  <h3 style="font-family:var(--fd);font-size:1.1rem;font-weight:600;color:var(--forest);margin-bottom:.5rem">Formation 100% gratuite</h3>
                  <p style="font-size:.84rem;color:var(--ink-60);line-height:1.6;margin-bottom:1rem">Grâce à nos partenaires et donateurs, cette formation est entièrement gratuite.</p>
                  <router-link to="/faire-un-don"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">Soutenir nos formations</button></router-link>
                </div>
              </div>
              <div class="sf-sidebar-card" style="margin-top:1.25rem">
                <div class="sf-sidebar-card__header"><h3>Statistiques</h3></div>
                <div class="sf-sidebar-card__body">
                  <div class="sf-stat-rows">
                    <div class="sf-stat-row"><span>Taux de réussite</span><strong style="color:var(--terra)">{{ formation.success_rate||95 }}%</strong></div>
                    <div class="sf-stat-row"><span>Diplômés</span><strong style="color:var(--terra)">{{ formation.graduates_count||850 }}+</strong></div>
                    <div class="sf-stat-row"><span>Entreprises partenaires</span><strong style="color:var(--terra)">{{ formation.partners_count||45 }}</strong></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { ArrowLeft, CheckCircle, Calendar, Clock, Users, Award, X } from 'lucide-vue-next'
export default {
  name:'FormationDetail',
  components:{ ArrowLeft, CheckCircle, Calendar, Clock, Users, Award, X },
  data(){return{registrationForm:{first_name:'',last_name:'',email:'',phone:'',motivation:''},registrationLoading:false,toastMessage:''}},
  computed:{...mapState({formation:s=>s.currentFormation,loading:s=>s.loading,error:s=>s.error})},
  watch:{'$route.params.slug':{handler(s){if(s)this.loadFormation(s)},immediate:true},toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchFormationBySlug','registerToFormation']),
    async loadFormation(s){try{await this.fetchFormationBySlug(s)}catch(e){console.error(e)}},
    async submitRegistration(){
      if(!this.formation)return
      const {first_name,last_name,email,phone,motivation}=this.registrationForm
      if(!first_name||!last_name||!email||!phone||!motivation){this.toastMessage='Veuillez remplir tous les champs';return}
      this.registrationLoading=true
      try{await this.registerToFormation({id:this.formation.id,formData:{...this.registrationForm,formation_id:this.formation.id}});this.toastMessage='Inscription réussie !';this.registrationForm={first_name:'',last_name:'',email:'',phone:'',motivation:''};window.scrollTo({top:0,behavior:'smooth'})}catch(e){this.toastMessage=e.response?.data?.message||'Erreur lors de l\'inscription'}finally{this.registrationLoading=false}
    },
    formatDate(d){return d?new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'}):'N/A'},
    getTrainerInitials(t){return t?.name?t.name.split(' ').map(n=>n[0]).join('').toUpperCase():'F'}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.sf-formation-badges{display:flex;flex-wrap:wrap;gap:.4rem;justify-content:center}
.fm-badge-hero{font-size:.72rem;padding:.3rem .8rem;border-radius:2px;letter-spacing:.06em}
.fm-badge-hero{background:rgba(255,255,255,.15);color:var(--cream);border:1px solid rgba(255,255,255,.25)}
.fm-badge-hero--muted{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.15)}
.fm-badge-hero--green{background:rgba(122,158,135,.3);color:var(--cream);border-color:rgba(122,158,135,.4)}
.fm-badge-hero--red{background:rgba(200,90,42,.3);border-color:rgba(200,90,42,.4)}
.sf-layout{display:grid;grid-template-columns:1fr 320px;gap:2rem;align-items:start}
@media(max-width:900px){.sf-layout{grid-template-columns:1fr}}
.sf-main{}
.sf-sidebar{}
.sf-check-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.7rem}
.sf-check-list li{display:flex;align-items:flex-start;gap:.6rem;font-size:.88rem;color:var(--ink-60);line-height:1.55}
.sf-check-list li :deep(svg){width:16px;height:16px;color:var(--terra);flex-shrink:0;margin-top:2px}
.sf-program-item{padding:.85rem 0;border-bottom:1px solid var(--ink-20)}
.sf-program-item:last-child{border-bottom:none}
.sf-program-item__head{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;margin-bottom:.6rem}
.sf-program-item__title{font-size:.92rem;font-weight:500;color:var(--ink)}
.sf-program-item__topics{display:flex;flex-wrap:wrap;gap:.35rem}
.fm-skill-tag{background:var(--forest);color:var(--cream);font-size:.75rem;padding:.3rem .75rem;border-radius:4px}
.sf-form-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
@media(max-width:600px){.sf-form-row{grid-template-columns:1fr}}
.sf-info-rows{display:flex;flex-direction:column;gap:1rem}
.sf-stat-rows{display:flex;flex-direction:column;gap:.6rem}
.sf-stat-row{display:flex;justify-content:space-between;align-items:center;padding:.4rem 0;border-bottom:1px solid var(--ink-20);font-size:.85rem;color:var(--ink-60)}
.sf-stat-row:last-child{border-bottom:none}
</style>
